import { useState } from "react";
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Fade,
  Grow,
  Zoom,
  Chip,
  Card,
  CardContent,
  Stack,
  Divider,
  alpha,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  Scale as ScaleIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { trucksData } from "../data/trucksData";

const steps = [
  { label: "Shipment Details", icon: <TruckIcon /> },
  { label: "Contact Information", icon: <PersonIcon /> },
  { label: "Review & Submit", icon: <CheckIcon /> },
];

const Booking = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1
    pickupLocation: "",
    deliveryLocation: "",
    cargoType: "",
    cargoWeight: "",
    selectedTruck: "",
    pickupDate: null,
    deliveryDate: null,

    // Step 2
    fullName: "",
    company: "",
    email: "",
    phone: "",
    address: "",

    // Step 3
    specialInstructions: "",
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const theme = useTheme();

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateCurrentStep = () => {
    if (activeStep === 0) {
      return (
        formData.pickupLocation &&
        formData.deliveryLocation &&
        formData.cargoType &&
        formData.cargoWeight &&
        formData.pickupDate &&
        formData.deliveryDate
      );
    }
    if (activeStep === 1) {
      return (
        formData.fullName &&
        formData.email &&
        formData.phone
      );
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setFormSubmitted(true);

    // Reset form after 5 seconds
    setTimeout(() => {
      setFormData({
        pickupLocation: "",
        deliveryLocation: "",
        cargoType: "",
        cargoWeight: "",
        selectedTruck: "",
        pickupDate: null,
        deliveryDate: null,
        fullName: "",
        company: "",
        email: "",
        phone: "",
        address: "",
        specialInstructions: "",
      });
      setActiveStep(0);
      setFormSubmitted(false);
    }, 5000);
  };

  const cargoTypes = [
    "General Freight",
    "Refrigerated Goods",
    "Construction Materials",
    "Liquids",
    "Hazardous Materials",
    "Oversized Load",
    "Furniture",
    "Electronics",
    "Agricultural Products",
    "Machinery & Equipment",
    "Automotive Parts",
    "Other",
  ];

  if (formSubmitted) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Grow in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 8 },
              textAlign: "center",
              borderRadius: 4,
              border: "2px solid",
              borderColor: "primary.main",
              bgcolor: "grey.50",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "primary.main",
                borderRadius: "50%",
                color: "white",
              }}
            >
              <CheckIcon sx={{ fontSize: 40 }} />
            </Box>

            <Typography variant="h2" gutterBottom fontWeight={800}>
              Thank You!
            </Typography>

            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
            >
              Your booking request has been submitted successfully. Our logistics specialist will contact you within 30 minutes with a detailed quote.
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: 3,
                borderLeft: "4px solid",
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Typography variant="body2">
                <strong>Reference Number:</strong> SFL-{Math.random().toString(36).substr(2, 8).toUpperCase()}
              </Typography>
            </Alert>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => setFormSubmitted(false)}
                sx={{ px: 4, py: 1.5, borderRadius: 3 }}
              >
                Submit Another Booking
              </Button>
              <Button
                variant="contained"
                component="a"
                href="tel:+27111234567"
                startIcon={<PhoneIcon />}
                sx={{ px: 4, py: 1.5, borderRadius: 3 }}
              >
                Call Now: +27 11 123 4567
              </Button>
            </Stack>
          </Paper>
        </Grow>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
        <Fade in timeout={600}>
          <Box>
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                mb: 2,
                "&::after": {
                  content: '""',
                  display: "block",
                  width: 80,
                  height: 4,
                  bgcolor: "primary.main",
                  borderRadius: 2,
                  mx: "auto",
                  mt: 3,
                },
              }}
            >
              Book a Shipment
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                maxWidth: 600,
                mx: "auto",
                mb: 3,
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Complete the form below for a fast, accurate quote. Our logistics experts will contact you within 30 minutes.
            </Typography>

            <Chip
              label="Quick Response Guaranteed"
              color="primary"
              icon={<CheckIcon />}
              sx={{
                fontWeight: 600,
                fontSize: "0.9rem",
                px: 2,
                py: 1,
                borderRadius: 3,
              }}
            />
          </Box>
        </Fade>
      </Box>

      {/* Main Form Container */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          mb: 6,
        }}
      >
        {/* Stepper Header */}
        <Box
          sx={{
            bgcolor: "grey.50",
            p: { xs: 3, md: 4 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-root": {
                padding: "0 8px",
              },
              "& .MuiStepLabel-label": {
                fontWeight: 600,
                fontSize: "0.9rem",
                "&.Mui-active": {
                  fontWeight: 700,
                  color: "primary.main",
                },
                "&.Mui-completed": {
                  fontWeight: 700,
                },
              },
              "& .MuiStepIcon-root": {
                width: 32,
                height: 32,
                "&.Mui-active": {
                  color: "primary.main",
                },
                "&.Mui-completed": {
                  color: "primary.main",
                },
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel icon={step.icon}>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: { xs: 3, md: 6 } }}>
          <form onSubmit={handleSubmit}>
            {/* Step 1: Shipment Details */}
            {activeStep === 0 && (
              <Grow in timeout={400}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 4 }}>
                      <LocationIcon sx={{ mr: 1, verticalAlign: "middle", color: "primary.main" }} />
                      Shipment Details
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Pickup Location"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Street address, city, province"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Delivery Location"
                      name="deliveryLocation"
                      value={formData.deliveryLocation}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Street address, city, province"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Cargo Type"
                      name="cargoType"
                      value={formData.cargoType}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InventoryIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    >
                      {cargoTypes.map((type) => (
                        <MenuItem key={type} value={type} sx={{ py: 1.5 }}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Cargo Weight"
                      name="cargoWeight"
                      type="number"
                      value={formData.cargoWeight}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="0"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScaleIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">kg</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Pickup Date"
                        value={formData.pickupDate}
                        onChange={(date) => handleDateChange("pickupDate", date)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Delivery Date"
                        value={formData.deliveryDate}
                        onChange={(date) => handleDateChange("deliveryDate", date)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Preferred Truck Type"
                      name="selectedTruck"
                      value={formData.selectedTruck}
                      onChange={handleChange}
                      variant="outlined"
                      helperText="Our experts will select the best truck if you're unsure"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TruckIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    >
                      <MenuItem value="">Not specified - Let us choose</MenuItem>
                      {trucksData
                        .filter((truck) => truck.available)
                        .map((truck) => (
                          <MenuItem key={truck.id} value={truck.type} sx={{ py: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                              <Box>
                                <Typography variant="body1" fontWeight={600}>
                                  {truck.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {truck.type} • {truck.capacity}
                                </Typography>
                              </Box>
                              <Chip
                                label={truck.dailyRate}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grow>
            )}

            {/* Step 2: Contact Information */}
            {activeStep === 1 && (
              <Grow in timeout={400}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 4 }}>
                      <PersonIcon sx={{ mr: 1, verticalAlign: "middle", color: "primary.main" }} />
                      Contact Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="John Smith"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Company Name (Optional)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="john@example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="+27 11 123 4567"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Full address (Optional)"
                      multiline
                      rows={3}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          alignItems: "flex-start",
                        },
                        "& .MuiInputAdornment-root": {
                          mt: 1.5,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grow>
            )}

            {/* Step 3: Review & Submit */}
            {activeStep === 2 && (
              <Zoom in timeout={400}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Alert
                      severity="info"
                      icon={<InfoIcon />}
                      sx={{
                        mb: 4,
                        borderRadius: 3,
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Please review your shipment details before submitting. Our logistics specialist will contact you within 30 minutes.
                      </Typography>
                    </Alert>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: "2px solid",
                        borderColor: "divider",
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 3 }}>
                          <TruckIcon sx={{ mr: 1, verticalAlign: "middle", color: "primary.main" }} />
                          Shipment Summary
                        </Typography>

                        <Stack spacing={2.5}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              From
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.pickupLocation}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              To
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.deliveryLocation}
                            </Typography>
                          </Box>

                          <Divider />

                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Cargo Details
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.cargoType} • {formData.cargoWeight} kg
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Preferred Truck
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.selectedTruck || "Not specified"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Dates
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              Pickup: {formData.pickupDate ? new Date(formData.pickupDate).toLocaleDateString() : "Not set"}
                              <br />
                              Delivery: {formData.deliveryDate ? new Date(formData.deliveryDate).toLocaleDateString() : "Not set"}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: "2px solid",
                        borderColor: "divider",
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 3 }}>
                          <PersonIcon sx={{ mr: 1, verticalAlign: "middle", color: "primary.main" }} />
                          Contact Details
                        </Typography>

                        <Stack spacing={2.5}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Name
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.fullName}
                            </Typography>
                          </Box>

                          {formData.company && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Company
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formData.company}
                              </Typography>
                            </Box>
                          )}

                          <Divider />

                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Contact Information
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formData.email}
                              <br />
                              {formData.phone}
                            </Typography>
                          </Box>

                          {formData.address && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Address
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formData.address}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Special Instructions"
                      name="specialInstructions"
                      value={formData.specialInstructions}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={4}
                      placeholder="Any special requirements, handling instructions, or additional information for your shipment..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Zoom>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 8 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  borderWidth: 2,
                  fontWeight: 600,
                  "&:hover": {
                    borderWidth: 2,
                  },
                }}
              >
                Back
              </Button>

              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    type="submit"
                    endIcon={<SendIcon />}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: "1rem",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Submit Booking Request
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    disabled={!validateCurrentStep()}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: "1rem",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </Paper>

      {/* Info Section */}
      <Alert
        severity="info"
        sx={{
          borderRadius: 3,
          borderLeft: "4px solid",
          borderColor: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Typography variant="body1" fontWeight={600} gutterBottom>
          Need immediate assistance?
        </Typography>
        <Typography variant="body2">
          For urgent shipments or same-day booking, please call our 24/7 logistics hotline at{" "}
          <Box component="span" fontWeight={700} color="primary.main">
            +27 11 123 4567
          </Box>
          . One of our specialists will assist you immediately.
        </Typography>
      </Alert>
    </Container>
  );
};

export default Booking;